import { Dimension, Product } from '@inter-ikea-storageone/range';
import styled from 'styled-components';
import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import { ProductIdentifiers, ProductType } from '@inter-ikea-storageone/range';
import { useSelector } from 'react-redux';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import { useCore } from 'contexts/core';
import { getSelectedEntity } from 'ducks/core';
import { getUnit } from 'filters/SizeFilter/utils';
import { findInvalidReplacements, getDisabledSizesDefault } from '../utils/findAllowedPartSizes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import SizeToggle from 'ui/SizeToggle/SizeToggle';
import theme from 'ui/theme';
import { useIntl } from 'react-intl';
import { isCornerFrameAllowingDoors } from '@inter-ikea-storageone/range-pax';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { isMobile } from 'utils/platform';
import { useBehaviorTracker } from 'contexts/behaviors';

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space.xl};
  flex-wrap: wrap;
`;

const StyledSizeToggle = styled(SizeToggle)`
  flex-basis: 100%;
`;
const InfoMessageWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space.s};
`;

const MobileWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.space.l};
`;

export const getReplaceableCornerSolutions = (products: Product[], selectedProduct: Product) => {
  const isMirrored = selectedProduct.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT);
  const corenerSolutions = products.filter(
    product =>
      product.type === ProductType.PAX_CORNER_SOLUTION_ASM &&
      product.design === selectedProduct.design
  );
  if (isMirrored) {
    return corenerSolutions.filter(product =>
      product.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT)
    );
  }
  return corenerSolutions;
};

export const getReplacableDimensionsForPart = (type: ProductType, dimension: Dimension) => (
  cornerSolutions: Product[],
  allProducts: Product[]
) => {
  const sizes = cornerSolutions.reduce((acc, product) => {
    const partsProducts = product.parts.map(part =>
      allProducts.find(p => p.coreProductId === part.id)
    );
    const addOnPart = partsProducts.find(p => p && p.type === type);
    if (!addOnPart) {
      return acc;
    }
    const size = addOnPart?.communicatedDimensions[dimension];
    if (!size) {
      return acc;
    }
    return {
      ...acc,
      [size]: addOnPart?.communicatedDimensionsLocalized[dimension]
    };
  }, {});
  return sizes;
};

export const getInitalFilterStates = (selectedProduct: Product | null, products: Product[]) => {
  const parts = selectedProduct?.parts.map(part => products.find(p => p.coreProductId === part.id));
  const addOn = parts?.find(part => part?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM);
  const frame = parts?.find(part => part?.type === ProductType.PAX_FRAME);
  const addOnDepth = addOn?.communicatedDimensions.depth;
  const frameWidth = frame?.communicatedDimensions.width;
  const frameDepth = frame?.communicatedDimensions.depth;
  const frameHeight = frame?.communicatedDimensions.height;

  return {
    addOnFrame: {
      depth: addOnDepth
    },
    frame: {
      width: frameWidth,
      depth: frameDepth,
      height: frameHeight
    }
  };
};

const getReplaceableProduct = (
  selectedProduct: Product,
  cornerSolutions: Product[],
  products: Product[],
  size: number,
  typeToReplace: ProductType,
  dimension: Dimension
) => {
  const product = cornerSolutions.find(product => {
    if (dimension === 'height') {
      return (
        product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width &&
        product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth &&
        product.communicatedDimensions.height === size
      );
    }

    const parts = product?.parts.map(part => products.find(p => p.coreProductId === part.id));
    const addOn = parts?.find(part => part?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM);
    const frame = parts?.find(part => part?.type === ProductType.PAX_FRAME);
    const selectedProductParts = selectedProduct?.parts.map(part =>
      products.find(p => p.coreProductId === part.id)
    );
    const selectedFramePart = selectedProductParts.find(p => p?.type === ProductType.PAX_FRAME);
    const selectedAddOnPart = selectedProductParts.find(
      p => p?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM
    );

    if (!selectedFramePart || !selectedAddOnPart) return false;
    let addOnIsEqual: boolean;
    let frameIsEqual: boolean;
    if (typeToReplace === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM) {
      addOnIsEqual = R.equals(
        { ...selectedAddOnPart?.communicatedDimensions, [dimension]: size },
        addOn?.communicatedDimensions
      );
      frameIsEqual = R.equals(
        frame?.communicatedDimensions,
        selectedFramePart.communicatedDimensions
      );
    } else {
      addOnIsEqual = R.equals(
        addOn?.communicatedDimensions,
        selectedAddOnPart.communicatedDimensions
      );
      frameIsEqual = R.equals(
        { ...selectedFramePart?.communicatedDimensions, [dimension]: size },
        frame?.communicatedDimensions
      );
    }
    return addOnIsEqual && frameIsEqual;
  });
  return product;
};

const ChangeCornerSolutionParts = () => {
  const { formatMessage } = useIntl();
  const [disabledSizes, setDisabledSizes] = useState(getDisabledSizesDefault());
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const behaviorTracker = useBehaviorTracker();

  const selectedEntity = useSelector(getSelectedEntity);
  const entity = selectedEntity && coreApi.entities.find(selectedEntity?.entityId);

  const selectedProduct = useSelector(getSelectedProduct);
  const filterState = getInitalFilterStates(selectedProduct, products);

  useEffect(() => {
    const updateDisabledSizes = () => {
      if (!selectedEntity) return;

      const split = selectedEntity.productId.split('-');
      const leftSolutionId = `${split[0]}-${split[1]}-asm`;
      const rightSolutionId = `${split[0]}-${split[1]}-mirrored-asm`;

      if (selectedProduct && selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
        const newDisabledSizes = findInvalidReplacements(coreApi, selectedEntity, products, {
          left: leftSolutionId,
          right: rightSolutionId
        });
        setDisabledSizes(newDisabledSizes);
      }
    };
    updateDisabledSizes();
    return coreApi.subscribe('history', updateDisabledSizes);
  }, [coreApi, products, selectedEntity, selectedProduct]);

  if (!selectedProduct || selectedProduct.type !== ProductType.PAX_CORNER_SOLUTION_ASM) {
    return null;
  }

  const cornerSolutions = getReplaceableCornerSolutions(products, selectedProduct);

  const addOnDepths = getReplacableDimensionsForPart(
    ProductType.PAX_ADD_ON_CORNER_FRAME_ASM,
    'depth'
  )(cornerSolutions, products);
  const frameDepths = getReplacableDimensionsForPart(ProductType.PAX_FRAME, 'depth')(
    cornerSolutions,
    products
  );
  const frameWidths = getReplacableDimensionsForPart(ProductType.PAX_FRAME, 'width')(
    cornerSolutions,
    products
  );
  const frameHeights = getReplacableDimensionsForPart(ProductType.PAX_FRAME, 'height')(
    cornerSolutions,
    products
  );

  const onChange = (
    size: number,
    currentSize: number | undefined,
    type: ProductType,
    dimension: 'depth' | 'width' | 'height'
  ) =>
    behaviorTracker.custom(() => {
      if (size === currentSize) {
        return;
      }
      const product = getReplaceableProduct(
        selectedProduct,
        cornerSolutions,
        products,
        size,
        type,
        dimension
      );
      if (product && selectedEntity) {
        const allowedMovementOfChildrenInMilliMeter =
          dimension === 'height' ? 0.1 : Number.MAX_VALUE;
        const replaceInvalidEntities = dimension === 'height' ? false : true;

        const newFrameEntity = coreApi.products.replaceEntityWithHierarchy(
          selectedEntity.entityId,
          product.coreProductId,
          replaceInvalidEntities,
          allowedMovementOfChildrenInMilliMeter
        );
        if (newFrameEntity) {
          coreApi.entities.resnapEntity(newFrameEntity);
          coreApi.entities.select(newFrameEntity);
        }
      }
    });

  const unit = getUnit([selectedProduct]);

  const depthSizes = Object.entries(frameDepths).map(([size, communicated]) => {
    return { value: parseInt(size, 10), text: `${communicated}`, unit: `${unit}` };
  });
  const widthSizes = Object.entries(frameWidths).map(([size, communicated]) => {
    return { value: parseInt(size, 10), text: `${communicated}`, unit: `${unit}` };
  });
  const heightSizes = Object.entries(frameHeights).map(([size, communicated]) => {
    return { value: parseInt(size, 10), text: `${communicated}`, unit: `${unit}` };
  });
  const addOnDepthSizes = Object.entries(addOnDepths).map(([size, communicated]) => {
    return { value: parseInt(size, 10), text: `${communicated}`, unit: `${unit}` };
  });

  const openCornerFrameInScene = entity && !isCornerFrameAllowingDoors(coreApi, entity);

  return isMobile() && filterState.frame.width ? (
    <MobileWrapper>
      <SizeToggle
        sizes={widthSizes}
        disabled={disabledSizes.frame.width.length > 0}
        currentSize={filterState.frame.width}
        onChangeSize={size =>
          onChange(size, filterState.frame.width, ProductType.PAX_FRAME, 'width')
        }
      />
    </MobileWrapper>
  ) : (
    <Box>
      <Body my="s" type={ThemeFontStyleTypeEnum.bodyMedium} fontWeight="bold">
        {formatMessage({ id: 'storageone.frame' })}
      </Body>
      <Flex>
        {filterState.frame.depth && (
          <SizeToggle
            title={formatMessage({ id: 'storageone.depth' })}
            boldTitle={false}
            sizes={depthSizes}
            disabled={disabledSizes.frame.depth.length > 0}
            currentSize={filterState.frame.depth}
            onChangeSize={size =>
              onChange(size, filterState.frame.depth, ProductType.PAX_FRAME, 'depth')
            }
          />
        )}
        {filterState.frame.width && (
          <SizeToggle
            title={formatMessage({ id: 'storageone.width' })}
            boldTitle={false}
            sizes={widthSizes}
            disabled={disabledSizes.frame.width.length > 0}
            currentSize={filterState.frame.width}
            onChangeSize={size =>
              onChange(size, filterState.frame.width, ProductType.PAX_FRAME, 'width')
            }
          />
        )}
        {filterState.frame.height && (
          <StyledSizeToggle
            title={formatMessage({ id: 'storageone.height' })}
            boldTitle={false}
            sizes={heightSizes}
            disabled={disabledSizes.frame.height?.length > 0}
            currentSize={filterState.frame.height}
            onChangeSize={size =>
              onChange(size, filterState.frame.height, ProductType.PAX_FRAME, 'height')
            }
            paddingTop={theme.space.md}
          />
        )}
      </Flex>
      <Body mb="s" type={ThemeFontStyleTypeEnum.bodyMedium} fontWeight="bold">
        {formatMessage({ id: 'storageone.addOnCornerUnit' })}
      </Body>
      {filterState.addOnFrame.depth && (
        <SizeToggle
          title={formatMessage({ id: 'storageone.depth' })}
          boldTitle={false}
          sizes={addOnDepthSizes}
          disabled={disabledSizes.addOnFrame.depth.length > 0}
          currentSize={filterState.addOnFrame.depth}
          onChangeSize={size =>
            onChange(
              size,
              filterState.addOnFrame.depth,
              ProductType.PAX_ADD_ON_CORNER_FRAME_ASM,
              'depth'
            )
          }
          paddingBottom={theme.space.xs}
        />
      )}
      {openCornerFrameInScene && (
        <InfoMessageWrapper>
          <SkapaInlineMessage
            body={formatMessage({ id: 'storageone.paxCornerDimensionWithoutDoorsInfoMessage' })}
            variant={InlineMessageVariantEnum.INFORMATIVE}
            ssrIcon={InformationCircle}
            subtle
          />
        </InfoMessageWrapper>
      )}
    </Box>
  );
};

export default ChangeCornerSolutionParts;
